import React, { RefObject, useCallback, useEffect, useRef } from 'react';
import VideoTile from './VideoTile.tsx';
import FloatingParticipantStrip from './FloatingParticipantStrip.tsx';
import { Participant, VideoBackgroundEffect } from '../types/meeting.types';
import { attachStreamToVideo, getParticipantScreenShareState } from '../utils/mediaUtils.ts';

interface VideoGridProps {
  participants: Participant[];
  localVideoRef: RefObject<HTMLVideoElement | null>;
  localScreenVideoRef: RefObject<HTMLVideoElement | null>;
  localCameraPreviewRef: RefObject<HTMLVideoElement | null>;
  localCameraStream?: MediaStream | null;
  localScreenStream?: MediaStream | null;
  videoRefs: { [key: string]: RefObject<HTMLVideoElement | null> | null };
  isVideoEnabled: boolean;
  selectedParticipantId: string;
  onSelectParticipant: (id: string) => void;
  isLocalScreenSharing?: boolean;
  videoBackgroundEffect?: VideoBackgroundEffect;
  transparentMode?: boolean;
  localDisplayStream?: MediaStream | null;
  isFullscreen?: boolean;
}

const VideoGrid: React.FC<VideoGridProps> = ({
  participants,
  localVideoRef,
  localScreenVideoRef,
  localCameraPreviewRef,
  localCameraStream,
  localScreenStream,
  videoRefs,
  isVideoEnabled,
  selectedParticipantId,
  onSelectParticipant,
  isLocalScreenSharing = false,
  videoBackgroundEffect = 'none',
  transparentMode = false,
  localDisplayStream = null,
  isFullscreen = false,
}) => {
  const count = participants.length;
  const remoteScreenVideoRef = useRef<HTMLVideoElement | null>(null);

  const screenSharingParticipant = participants.find((participant) =>
    getParticipantScreenShareState(participant.stream, participant.isScreenSharing)
  );
  const hasScreenShare = isLocalScreenSharing || !!screenSharingParticipant;

  const bindRemoteScreenRef = useCallback((node: HTMLVideoElement | null) => {
    remoteScreenVideoRef.current = node;
    if (node && screenSharingParticipant?.stream) {
      attachStreamToVideo(node, screenSharingParticipant.stream);
    }
  }, [screenSharingParticipant?.stream]);

  const bindLocalScreenRef = useCallback((node: HTMLVideoElement | null) => {
    if (localScreenVideoRef && 'current' in localScreenVideoRef) {
      localScreenVideoRef.current = node;
    }
    if (node && localScreenStream) {
      attachStreamToVideo(node, localScreenStream);
    }
  }, [localScreenStream, localScreenVideoRef]);

  useEffect(() => {
    if (!isLocalScreenSharing) return;
    attachStreamToVideo(localScreenVideoRef.current, localScreenStream);
    attachStreamToVideo(localCameraPreviewRef.current, localCameraStream);
  }, [isLocalScreenSharing, localScreenStream, localCameraStream, localScreenVideoRef, localCameraPreviewRef]);

  useEffect(() => {
    if (isLocalScreenSharing || !screenSharingParticipant?.stream) return;
    attachStreamToVideo(remoteScreenVideoRef.current, screenSharingParticipant.stream);
  }, [isLocalScreenSharing, screenSharingParticipant?.stream]);

  const renderParticipantTile = (participant: Participant, size: 'small' | 'normal' = 'normal') => {
    const isSharingUser = getParticipantScreenShareState(participant.stream, participant.isScreenSharing)
      || (participant.isCurrentUser && isLocalScreenSharing);

    const tileVideoRef = participant.isCurrentUser
      ? (isLocalScreenSharing ? localCameraPreviewRef : localVideoRef)
      : (videoRefs[participant.id] as RefObject<HTMLVideoElement | null>);

    return (
      <VideoTile
        key={participant.id}
        participant={participant}
        videoRef={tileVideoRef}
        isLocalVideo={participant.isCurrentUser}
        isVideoEnabled={participant.isCurrentUser ? isVideoEnabled : participant.isVideoEnabled}
        isSelected={participant.id === selectedParticipantId}
        onClick={() => onSelectParticipant(participant.id)}
        size={size}
        showCameraInsteadOfScreen={isSharingUser}
        displayStream={
          participant.isCurrentUser
            ? (isLocalScreenSharing
                ? localCameraStream
                : (localDisplayStream ?? localCameraStream ?? participant.stream))
            : undefined
        }
        forceLandscapeFrame
        videoBackgroundEffect={participant.isCurrentUser ? videoBackgroundEffect : 'none'}
        transparentMode={participant.isCurrentUser ? transparentMode : false}
        backgroundProcessed={
          participant.isCurrentUser &&
          !isLocalScreenSharing &&
          videoBackgroundEffect !== 'none'
        }
      />
    );
  };

  if (hasScreenShare) {
    return (
      <div className="relative h-full w-full min-h-0 bg-black" data-recording-tile data-recording-bg="black">
        <div className="absolute inset-0" data-recording-tile data-recording-bg="black">
          {isLocalScreenSharing ? (
            <>
              <video
                ref={bindLocalScreenRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-contain bg-black"
              />
              <div className="absolute bottom-4 left-4 bg-green-600/90 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center gap-2">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="text-white text-sm font-medium">Ekranınızı paylaşıyorsunuz</span>
              </div>
            </>
          ) : screenSharingParticipant ? (
            <>
              <video
                ref={bindRemoteScreenRef}
                autoPlay
                playsInline
                className="w-full h-full object-contain bg-black"
              />
              <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-lg">
                <span className="text-white text-sm font-medium">
                  {screenSharingParticipant.name} ekranını paylaşıyor
                </span>
              </div>
            </>
          ) : null}
        </div>

        <FloatingParticipantStrip defaultCorner="top-right">
          {participants.map((participant) => renderParticipantTile(participant, 'small'))}
        </FloatingParticipantStrip>
      </div>
    );
  }

  if (count === 1) {
    const participant = participants[0];
    return (
      <div
        className={`h-full w-full min-h-0 flex items-center justify-center bg-black ${
          isFullscreen ? 'p-3 sm:p-5' : 'p-2'
        }`}
      >
        <div
          className={
            isFullscreen
              ? 'w-full h-full flex items-center justify-center'
              : 'w-full max-w-5xl aspect-video'
          }
        >
          <div
            className={
              isFullscreen
                ? 'w-full h-full max-w-[min(100%,calc((100vh-11rem)*16/9))] max-h-[calc(100vh-11rem)] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10'
                : 'w-full h-full'
            }
          >
            {renderParticipantTile(participant, isFullscreen ? 'large' : 'normal')}
          </div>
        </div>
      </div>
    );
  }

  const getGridClass = () => {
    if (count === 2) return 'grid-cols-1 sm:grid-cols-2';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-2 md:grid-cols-3';
    if (count <= 9) return 'grid-cols-2 sm:grid-cols-3';
    return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
  };

  const containerClass =
    count > 6
      ? 'h-full w-full min-h-0 overflow-y-auto p-2'
      : 'h-full w-full min-h-0 flex items-center justify-center p-2';

  return (
    <div className={containerClass}>
      <div className={`grid ${getGridClass()} gap-2 md:gap-3 w-full max-w-7xl mx-auto`}>
        {participants.map((participant) => (
          <div key={participant.id} className="w-full aspect-video">
            {renderParticipantTile(participant, 'normal')}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoGrid;
